"use client";

import { useState, useEffect } from "react";
import { X, Phone } from "lucide-react";
import { useGeo } from "../context/GeoContext";
import { generateCookieId, getClientIp } from "@/app/utils/clientInfo";
import { useIdelioCall } from "@/app/hooks/useIdelioCall";


const FORM_FIELDS = {
  LASTNAME: process.env.NEXT_PUBLIC_SF_LASTNAME_ID,
  FIRSTNAME: process.env.NEXT_PUBLIC_SF_FIRSTNAME_ID,
  ACCOUNT_ID: process.env.NEXT_PUBLIC_SF_ACCOUNT_ID_ID,
  PHONE_INDICATOR: process.env.NEXT_PUBLIC_SF_PHONE_INDICATOR_ID,
  ORGID: "orgid",
  RECORD_TYPE: "recordType",
  TYPE: "type",
  PRIORITY: "priority",
  ENCODING: "encoding",
  PHONE: "phone",
} as const;

export default function CallBack({ onClose }: { onClose: () => void }) {
  const { agency } = useGeo();

  const { sendIdelioCall } = useIdelioCall();

  const [lastname, setLastname] = useState("");
  const [firstname, setFirstname] = useState("");
  const [phone, setPhone] = useState("");

  const [cookieId] = useState(generateCookieId());
  const [ip, setIp] = useState("0.0.0.0");



  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get IP for Idelio
  useEffect(() => {
    getClientIp().then((v) => v && setIp(v));
  }, []);

  // ----------------------------------------------------------------------
  //                      HANDLE SUBMIT
  // ----------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!lastname.trim()) return setError("Veuillez saisir votre nom.");
    if (!firstname.trim()) return setError("Veuillez saisir votre prénom.");
    if (!phone.trim()) return setError("Veuillez saisir votre téléphone.");
    if (!agency) return setError("Aucune agence détectée.");

    setError(null);
    setLoading(true);

    // ----- Collecte UTM -----
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source");
    const utmCampaign = params.get("utm_campaign");
    const utmContent = params.get("utm_content");
    const utmTerm = params.get("utm_term");
    const utmMedium = params.get("utm_medium");
    const gclid = params.get("gclid");

    try {
      // ----------------------------------------------------------------------
      //                      1️ APPEL IDELIO
      // ----------------------------------------------------------------------
      const idelioPayload = {
        agenceId: Number(agency.id),
        prospect: {
          pays: "FR",
          numero: phone,
          ip,
          cookie: cookieId,
          nom: lastname,
          prenom: firstname,
        },
      };

      console.log("Payload Idelio envoyé :", idelioPayload);

      try {
        await sendIdelioCall(idelioPayload);
        console.log("Idelio OK");
      } catch (err) {
        console.error(" Idelio erreur (mais on continue) :", err);
      }

      //const FIXED_AGENCY_ID_SF = "001AW00000tIcAQYA0";
      // ----------------------------------------------------------------------
      //                      2️⃣ ENVOI À STRAPI
      // ----------------------------------------------------------------------
      const payload = {
        data: {
          // Champs visibles
          lastname,
          firstname,
          phone,
          agencyName: agency?.name ?? "",
          agencyIdSalesforce: agency?.idSalesforce ?? "",
          //agencyIdSalesforce: FIXED_AGENCY_ID_SF,
          AccountID: agency?.idSalesforce,

          // Champs Salesforce hidden (Strapi va les forward)
          orgid: process.env.NEXT_PUBLIC_SF_ORG_ID_ID,
          recordType: process.env.NEXT_PUBLIC_SF_RECORD_TYPE_ID,
          type: "Agence",
          priority: process.env.NEXT_PUBLIC_SF_PRIORITY_ID,
          encoding: "UTF-8",

          // Indicatif téléphone
          ...(FORM_FIELDS.PHONE_INDICATOR && {
            [FORM_FIELDS.PHONE_INDICATOR]: "FR",
          }),

          // Champ normalisé pour le backend
          phone_indicator: "FR",

          // UTM
          utm_source: utmSource || "",
          utm_campaign: utmCampaign || "",
          utm_content: utmContent || "",
          utm_term: utmTerm || "",
          utm_medium: utmMedium || "",
          gclid: gclid || "",
        },
      };

      console.log(" Payload STRAPI envoyé :", payload);

      const endpoint = process.env.NEXT_PUBLIC_APPEL_IMMEDIAT_ENDPOINT!;
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        console.error(" Erreur Strapi :", err);
        throw new Error(err.error?.message || "Erreur serveur Strapi");
      }

      console.log(" Strapi OK");

      setSubmitted(true);
      setLastname("");
      setFirstname("");
      setPhone("");

      setTimeout(() => onClose(), 2500);
    } catch (err) {
      console.error(" Erreur callback:", err);
      setError("Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------------------
  //                      UI
  // ----------------------------------------------------------------------
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-2xl p-8 relative">

        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500">
          <X size={26} />
        </button>

        <h2 className="text-2xl font-semibold text-center mb-6">
          Être appelé immédiatement
        </h2>

        {!submitted ? (
     
          <form onSubmit={handleSubmit} className="space-y-6" id="idelio_form">

            {/* Hidden Salesforce Fields */}
            <input type="hidden" name="orgid" value={process.env.NEXT_PUBLIC_SF_ORG_ID_ID} />
            <input type="hidden" name="recordType" value={process.env.NEXT_PUBLIC_SF_RECORD_TYPE_ID} />
            <input type="hidden" name="type" value="Agence" />
            <input type="hidden" name="priority" value={process.env.NEXT_PUBLIC_SF_PRIORITY_ID} />
            <input type="hidden" name="encoding" value="UTF-8" />


            {/* Nom / Prénom */}

            <div className="flex gap-4">
              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom <span className="text-secondary">*</span></label>
                <input
                  type="text"
                  id={FORM_FIELDS.LASTNAME}
                  name={FORM_FIELDS.LASTNAME}
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}

                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primaryGreen"
                  required
                />
              </div>

              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom <span className="text-secondary">*</span></label>
                <input
                  id={FORM_FIELDS.FIRSTNAME}
                  name={FORM_FIELDS.FIRSTNAME}
                  type="text"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primaryGreen"
                  required
                />
              </div>
            </div>



            {/* Téléphone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone <span className="text-secondary">*</span></label>
              <input
                type="tel"
                id={FORM_FIELDS.PHONE}
                name={FORM_FIELDS.PHONE}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primaryGreen"
                required
              />
            </div>

            {error && <p className="text-secondary">{error}</p>}

            <button
              type="submit"
              className="w-full py-3 bg-secondary text-white rounded-lg flex justify-center gap-2"
              disabled={loading}
            >
              {loading ? "Envoi..." : <><Phone size={18} /> APPEL IMMÉDIAT</>}
            </button>
          </form>
        ) : (
          <p className="text-primaryGreen text-center">Merci ! Nous allons vous rappeler.</p>
        )}
      </div>
    </div>
  );
}
